import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import aiAgentStore from "Stores/AIAgentStore";
import MessageBridge, { MessageTypes } from "Utils/MessageBridge";
import GeminiService from "../../Services/GeminiService";
import Utils from "Utils/utils";
import { MP_EVENT } from "static/constant";
import userDetail from "Stores/userDetail";
import useRotatingMessages from "Components/common/hooks/useRotatingMessages";
import SparkleIcon from "Components/common/SparkleIcon";

import "./AIAgent.scss";

const AI_LOADING_MESSAGES = [
  "Understanding your question…",
  "Searching across Salesgear…",
  "Pulling the right context…",
  "Composing your answer…"
];

// Empty-state starters. Each fires handleChipClick(query) — representative
// how-to / navigation questions that teach what this assistant is for.
const SUGGESTED_PROMPTS = [
  { icon: "list_alt", label: "How do I create a sequence?", query: "How do I create a sequence?" },
  { icon: "extension", label: "How to integrate with Salesforce?", query: "How do I integrate with Salesforce?" },
  { icon: "mail", label: "Connect my email inbox", query: "How do I connect my email inbox?" },
  { icon: "account_balance_wallet", label: "What are credits, and how are they used?", query: "What are credits, and how are they used?" }
];

const AIAgentPanel = observer(({ iframeRef }) => {
  const { isOpen, toggle } = aiAgentStore;
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const handleChipClickRef = useRef(null);
  const rotatingMessage = useRotatingMessages(AI_LOADING_MESSAGES, loading, 1800);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [aiAgentStore.messages.length, loading, isOpen]);

  const handleRedirect = (path) => {
    console.log("Redirecting to:", path);
    MessageBridge.sendToApp(iframeRef, MessageTypes.REDIRECT, { path });
  };

  const handleTalkToHuman = () => {
    MessageBridge.sendToApp(iframeRef, MessageTypes.SHOW_INTERCOM_CHAT);
  };

  // Helper to parse bold text within a string
  const parseBold = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Inline parsing for a single line: [links](path) + **bold**.
  const parseInline = (text) => {
    if (!text) return null;
    // Regex to match [Link Text](path)
    const regex = /(\[.*?\]\(.*?\))/g;
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check for Link
      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        const url = linkMatch[2];
        const isSupportLink = url.startsWith("https://support.salesgear.io");

        if (isSupportLink) {
          return (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="ai-link"
              title={`Open ${linkMatch[1]} in new tab`}
            >
              {linkMatch[1]}
              <span className="material-icons-outlined ai-link__extIcon">
                open_in_new
              </span>
            </a>
          );
        }

        return (
          <span
            key={index}
            className="ai-link"
            onClick={() => handleRedirect(url)}
            title={`Go to ${linkMatch[1]}`}
          >
            {linkMatch[1]}
          </span>
        );
      }

      // If not a link, parse for Bold
      return <span key={index}>{parseBold(part)}</span>;
    });
  };

  // Render the message body line-by-line so markdown bullets ("* "/"- ")
  // become a real list instead of leaking raw asterisks. Blank lines become
  // paragraph spacers; everything else is a normal line with inline parsing.
  const renderMessage = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const blocks = [];
    let bullets = [];
    let key = 0;

    const flushBullets = () => {
      if (bullets.length) {
        blocks.push(
          <ul className="aiList" key={`ul-${key++}`}>
            {bullets.map((item, i) => (
              <li key={i}>{parseInline(item)}</li>
            ))}
          </ul>
        );
        bullets = [];
      }
    };

    lines.forEach((line) => {
      const bulletMatch = line.match(/^\s*[*-]\s+(.*)$/);
      if (bulletMatch) {
        bullets.push(bulletMatch[1]);
        return;
      }
      flushBullets();
      if (line.trim() === "") {
        blocks.push(<div className="aiBreak" key={`b-${key++}`} />);
      } else {
        blocks.push(
          <div className="aiLine" key={`l-${key++}`}>
            {parseInline(line)}
          </div>
        );
      }
    });
    flushBullets();
    return blocks;
  };

  const sendMessage = async (text) => {
    aiAgentStore.addMessage({ text, sender: 'You', type: 'sent' });
    setLoading(true);
    Utils.mixpanelTrack(MP_EVENT.AI_ASSISTANT_QUESTION_SUBMITTED, {
      question: text,
      questionLength: text.length,
      userEmail: userDetail?.userInfo?.email || ""
    });
    try {
      const response = await GeminiService.chat(text);
      aiAgentStore.addMessage({ text: response, sender: 'AI', type: 'received' });
    } catch (error) {
      console.error("AI Error:", error);
      aiAgentStore.addMessage({ text: `Error: ${error.message}`, sender: 'System', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const userMsg = inputValue.trim();
    setInputValue("");
    await sendMessage(userMsg);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  const handleChipClick = async (text) => {
    if (loading) return;
    setInputValue("");
    await sendMessage(text);
  };

  const renderMessageClass = (sender) => {
    if (sender === 'You') return 'from-user';
    if (sender === 'App') return 'from-app';
    return 'from-ai';
  };

  // Keep ref pointing at the latest handleChipClick so the pendingQuery
  // effect always invokes the current closure (no stale state).
  handleChipClickRef.current = handleChipClick;

  useEffect(() => {
    if (aiAgentStore.pendingQuery && !loading) {
      const q = aiAgentStore.consumePendingQuery();
      if (q && handleChipClickRef.current) {
        handleChipClickRef.current(q);
      }
    }
  }, [aiAgentStore.pendingQuery, loading]);

  return (
    <div className={`aiAgentPanel dark-mode ${isOpen ? "open" : ""}`}>
      <div className="agentHeader">
        <div className="agentTitle">
          <span className="aiAvatar">
            <SparkleIcon />
          </span>
          <h3>Assistant</h3>
        </div>
        <div className="header-controls">
          <span
            className="material-icons-outlined closeBtn"
            onClick={toggle}
            title="Close Panel"
          >
            close
          </span>
        </div>
      </div>

      <div className="agentContent">
        {aiAgentStore.messages.length === 0 && (
          <div className="welcomeMessage">
            <span className="aiAvatar lg">
              <SparkleIcon />
            </span>
            <h4>How can I help you today?</h4>
            <p>
              I'm your in-app guide — ask me how to do something, find a
              setting, or get somewhere fast. For details on people and
              companies, use search.
            </p>
            <div className="suggestLabel">Try asking</div>
            <div className="suggestions">
              {SUGGESTED_PROMPTS.map(prompt => (
                <div
                  key={prompt.query}
                  className="chip"
                  onClick={() => handleChipClick(prompt.query)}
                >
                  <i className="material-icons-outlined chipIcon">
                    {prompt.icon}
                  </i>
                  <span>{prompt.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="messagesList">
          {aiAgentStore.messages.map((msg, index) => (
            <div key={index} className={`message ${renderMessageClass(msg.sender)}`}>
              {msg.sender !== 'You' && (
                <div className="aiSender">
                  <span className="aiAvatar sm">
                    <SparkleIcon />
                  </span>
                  <strong>{msg.sender === 'AI' ? 'Assistant' : msg.sender}</strong>
                </div>
              )}
              <div className="aiMessageBody">
                {renderMessage(msg.text)}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message from-ai loading">
              <div className="aiSender">
                <span className="aiAvatar sm">
                  <SparkleIcon />
                </span>
                <strong>Assistant</strong>
              </div>
              <div className="aiThinking">{rotatingMessage}</div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="talkToHumanRow">
        <button
          type="button"
          className="talkToHumanBtn"
          onClick={handleTalkToHuman}
          title="Open Intercom chat"
        >
          <i className="material-icons-outlined">chat</i>
          <span>Talk to a human</span>
        </button>
      </div>

      <div className="agentInputArea">
        <div className="inputWrapper">
          <input
            type="text"
            placeholder="Ask a question or type a command..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            autoFocus
            maxLength={2000}
          />
          <button
            className="sendBtn"
            onClick={handleSend}
            disabled={!inputValue.trim() || loading}
          >
            <span className="material-icons-round">arrow_upward</span>
          </button>
        </div>
        <div className="charCount">
          {inputValue.length}/2000
        </div>
      </div>
    </div>
  );
});

export default AIAgentPanel;
